import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

/**
 * Sprint-4-UX-Carry-Over: Lock-State-Banner für /settings/secrets.
 *
 * Hintergrund: Backend verschlüsselt PVE-Tokens mit dem Master-Passwort
 * (Argon2id-derived KEK, in-Memory). Wenn der KEK-Cache nicht warm ist
 * (Tool-Restart, oder explizit Logout+Re-Login mit anderem Passwort),
 * antwortet `GET /secrets/pve` mit 401/423.
 *
 * Wir mappen das auf 3 Zustände:
 *  - 'loading'  : initial fetch
 *  - 'unlocked' : 200 OK
 *  - 'locked'   : 401 Unauthorized (allgemeine Auth) ODER 423 Locked
 *                 (Sonderfall: Backend-Code muss das ggf. liefern)
 *
 * `unlock()` triggert ein refetch nachdem `useUnlockBackend` (= POST /auth/login)
 * erfolgreich war.
 */

export type LockState = 'loading' | 'unlocked' | 'locked';

export interface UseSecretsLockStateResult {
  state: LockState;
  /** True, wenn der Banner explizit angezeigt werden soll. */
  isLocked: boolean;
  /** Letzter Statuscode vom Probe-Call (zum Tooltip / Logging). */
  lastStatus: number | null;
  /** Manuell neu prüfen (z.B. nach Inline-Login). */
  refresh: () => Promise<void>;
}

interface ProbeResult {
  ok: boolean;
  status: number;
}

const LOCK_PROBE_KEY = ['secrets', 'lock-probe'] as const;

export function useSecretsLockState(): UseSecretsLockStateResult {
  const qc = useQueryClient();
  const probe = useQuery<ProbeResult>({
    queryKey: LOCK_PROBE_KEY,
    queryFn: async () => {
      try {
        await api.get('/secrets/pve');
        return { ok: true, status: 200 };
      } catch (err) {
        if (err instanceof ApiError) {
          // 401/423 = Lock-Signal. 401 wird zwar global durch forceLogout()
          // abgefangen, aber bei React-Query läuft trotzdem dieser Branch
          // (Promise rejected). Der Logout ändert dann den Auth-State,
          // ProtectedRoute leitet aufs Login um - kein Konflikt.
          if (err.status === 401 || err.status === 423) {
            return { ok: false, status: err.status };
          }
        }
        // Andere Fehler werfen wir durch -> Banner zeigt "loading"-Zustand.
        throw err;
      }
    },
    // Retry bei 4xx ohnehin off via shouldRetry; staleTime kurz, damit
    // refresh() nach Re-Login sicher refetcht.
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  });

  const isLoading = probe.isLoading || (probe.isFetching && probe.data === undefined);
  const lastStatus = probe.data?.status ?? null;
  let state: LockState;
  if (isLoading) state = 'loading';
  else if (probe.data?.ok) state = 'unlocked';
  else state = 'locked';

  const refresh = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: LOCK_PROBE_KEY });
    await probe.refetch();
  };

  return {
    state,
    isLocked: state === 'locked',
    lastStatus,
    refresh,
  };
}
